import React from 'react';
import * as R from 'ramda';
import * as Yup from 'yup';
import { withFormik } from 'formik';
import { pure, branch, compose, withProps, lifecycle, withHandlers } from 'react-recompose';
// components
import { FormFooter } from '../../components/form-footer';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// forms
import { FormSection } from '../';
// hocs
import { withAsyncSequence } from '../../hocs';
// components/forms
import { Fieldset2 } from '../formik';
import {
  defaultLatLngFields,
  defaultContactFields,
  defaultLocationFields,
  contractBillToLocationSettings,
  getBillToLocationSectionSettings,
  getLocationValidationSchemaObject,
  contractBillToLocationValidationSchemaObject,
} from '../settings/location-formik-settings';
//////////////////////////////////////////////////

const LocationSections = pure((props) => {
  const { formType, fieldOptions, optionsForSelect } = props;

  const fieldSettings = R.equals(formType, GC.LOCATION_FORM_TYPE_CONTRACT_BILL_TO_LOCATION)
    ? contractBillToLocationSettings
    : getBillToLocationSectionSettings(fieldOptions);

  return (
    <FormSection>
      {fieldSettings.map(({ fields }, index) => (
        <Fieldset2
          {...G.getFormikProps(props)}
          {...optionsForSelect}
          key={index}
          fields={fields}
          fieldsWrapperStyles={{ mt: 25, px: 15, justifyContent: 'space-between' }}
        />
      ))}
    </FormSection>
  );
});

const enhance = compose(
  withProps(() => ({
    sequenceConfigName: GC.TEMPLATES_LOCATION_TEMPLATE_ID_SEQUENCE,
    autogenerateConfigName: GC.TEMPLATES_LOCATION_TEMPLATE_ID_AUTOGENERATED,
    configsNamesArray: [
      GC.TEMPLATES_LOCATION_TEMPLATE_ID_SEQUENCE,
      GC.TEMPLATES_LOCATION_TEMPLATE_ID_AUTOGENERATED,
    ],
  })),
  branch(({ fieldOptions }) => G.isNilOrFalse(G.getPropFromObject('withoutTemplateId', fieldOptions)),
    withAsyncSequence,
  ),
  withFormik({
    enableReinitialize: true,
    validationSchema: () => Yup.lazy(({ saveAsTemplate }) =>
      Yup.object().shape(getLocationValidationSchemaObject(saveAsTemplate)),
    ),
    mapPropsToValues: (props) => {
      const { initialValues, asyncSequence, searchedValues } = props;

      const defaultFields = G.ifElse(
        G.isNotNilAndNotEmpty(asyncSequence),
        R.assoc(GC.FIELD_TEMPLATE_ID, asyncSequence, defaultLocationFields),
        defaultLocationFields,
      );

      return G.setInitialLocationFormikValues(
        {
          ...defaultFields,
          ...defaultLatLngFields,
          ...defaultContactFields,
        },
        initialValues,
        searchedValues,
      );
    },
    handleSubmit: (values, { props }) => {
      const { asyncSequence, submitHandler } = props;

      if (R.and(
        R.propEq(asyncSequence, GC.FIELD_TEMPLATE_ID, values),
        G.isTrue(G.getPropFromObject('saveAsTemplate', values)),
      )) {
        return submitHandler(R.assoc(GC.FIELD_TEMPLATE_ID, null, values));
      }

      submitHandler(values);
    },
  }),
  withHandlers({
    handleSubmitAndSaveLocation: ({ setFieldValue }) => () =>
      setFieldValue('saveAsTemplate', true),
    handleSetLatLongToLocation: (props) => async () => {
      const { setValues, initialValues } = props;

      const address = R.compose(
        R.join(', '),
        R.values,
        R.filter(G.isNotNilAndNotEmpty),
        R.pick([
          GC.FIELD_ZIP,
          GC.FIELD_CITY,
          GC.FIELD_STATE,
          GC.FIELD_COUNTRY,
          GC.FIELD_ADDRESS_1,
        ]),
      )(initialValues);

      if (G.isNilOrEmpty(address)) return;

      const lat = G.getPropFromObject(GC.FIELD_LATITUDE, initialValues);
      const lon = G.getPropFromObject(GC.FIELD_LONGITUDE, initialValues);

      if (R.and(G.isNotNilAndNotEmpty(lat), G.isNotNilAndNotEmpty(lon))) return;

      // TODO: skip this call on did mount
      const { latitude, longitude } = await G.geocodeByPlaceAddress(address, 'forms -> BillToLocationForm');

      setValues(R.mergeRight(initialValues, { latitude, longitude }));
    },
  }),
  lifecycle({
    componentDidMount() {
      const { fieldOptions, handleSetLatLongToLocation } = this.props;

      if (R.not(G.getPropFromObject('withoutLatLng', fieldOptions))) handleSetLatLongToLocation();
    },
  }),
  pure,
);

const BillToLocationForm = (props) => {
  const {
    handleSubmit,
    fieldOptions,
    setFieldValue,
    submitBtnText,
    submitAndSaveLocation,
    handleSubmitAndSaveLocation,
  } = props;

  return (
    <form onSubmit={handleSubmit}>
      <LocationSections {...props} />
      <FormFooter
        boxStyles={{ p: 15 }}
        submitBtnText={submitBtnText}
        submitAction={() => {
          if (G.isTrue(G.getPropFromObject('withoutTemplateId', fieldOptions))) {
            setFieldValue('saveAsTemplate', false);
          } else {
            setFieldValue('saveAsTemplate', R.not(submitAndSaveLocation));
          }
        }}
        actionButtons={G.ifElse(
          G.isTrue(submitAndSaveLocation),
          [{
            type: 'submit',
            action: handleSubmitAndSaveLocation,
            buttonStyles: { width: 'max-content' },
            displayText: G.getWindowLocale('actions:submit-and-save-location', 'Submit and Save Location'),
          }],
          null,
        )}
      />
    </form>
  );
};

const contractBillToLocationEnhance = compose(
  withFormik({
    enableReinitialize: true,
    handleSubmit: (values, { props }) => props.submitHandler(values),
    validationSchema: Yup.object().shape(contractBillToLocationValidationSchemaObject),
    mapPropsToValues: ({ initialValues, searchedValues }) => G.setInitialLocationFormikValues(
      R.assoc(GC.FIELD_CONTACTS, [], defaultLocationFields),
      initialValues,
      searchedValues,
    ),
  }),
);

export const ContractBillToLocationForm = contractBillToLocationEnhance(BillToLocationForm);

export default enhance(BillToLocationForm);
